import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PrintIcon from "@mui/icons-material/Print";
import PaymentsIcon from "@mui/icons-material/Payments";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const today = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const money = (value) =>
  Number(value ?? 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const amountValue = (value) => {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number : 0;
};

const getResponseData = (response) => response?.data?.data ?? response?.data ?? {};

const getYearLabel = (year) => year?.year_label || `${year?.year_number ?? ""} Year`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
  "Content-Type": "application/json",
});

export default function FeeReceiptTermsDialog({ selectedStudent, onPaymentSuccess }) {
  const user = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  const [termDialogOpen, setTermDialogOpen] = useState(false);
  const [termLoading, setTermLoading] = useState(false);
  const [termSaving, setTermSaving] = useState(false);
  const [termYears, setTermYears] = useState([]);
  const [termError, setTermError] = useState("");

  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState(null);

  const [payment, setPayment] = useState({
    amount: "",
    payment_mode: "CASH",
    transaction_number: "",
    transaction_date: today(),
    remarks: "",
  });

  const studentRollno =
    selectedStudent?.student_rollno ??
    selectedStudent?.student_roll_no ??
    selectedStudent?.rollno ??
    "";

  const studentName = selectedStudent?.student_name ?? selectedStudent?.name ?? "";

  const studentBranch = selectedStudent?.branch.branch_name ?? selectedStudent?.branch ?? "";
  const getTermFeeSummary = async () => {
    if (!clientId || !collegeId) {
      throw new Error("Client ID or college ID is missing from the session.");
    }

    if (!studentRollno) {
      throw new Error("Please select a student first.");
    }

    const url =
      `${API_BASE_URL}/api/fee-receipt-terms/` +
      `${clientId}/${collegeId}/student/` +
      encodeURIComponent(studentRollno);

    const response = await axios.get(url, {
      headers: getAuthHeaders(),
    });

    const data = getResponseData(response);
    const years = Array.isArray(data?.years) ? data.years : [];

    return { data, years };
  };

  const openTermFeeDialog = async () => {
    if (!studentRollno) {
      await Swal.fire({
        title: "Select Student",
        text: "Please search and select a student first.",
        icon: "warning",
        confirmButtonColor: "#1976d2",
        didOpen: () => {
          Swal.getContainer()?.style.setProperty("z-index", "20000", "important");
        },
      });
      return;
    }

    setTermLoading(true);
    setTermError("");

    try {
      // Fetch fee terms for this student's branch and admitted batch.
      const result = await getTermFeeSummary();
      const years = result.years;

      const hasTermConfiguration = years.some(
        (year) =>
          Array.isArray(year.terms) &&
          year.terms.some((term) => amountValue(term.configured_fee) > 0)
      );

      if (!hasTermConfiguration) {
        const branchName =
          selectedStudent?.branch?.branch_name ||
          (typeof selectedStudent?.branch === "string" ? selectedStudent.branch : "") ||
          "Selected Branch";

        const admittedBatch =
          selectedStudent?.admittedbatch?.admittedbatch ||
          selectedStudent?.admittedbatch ||
          selectedStudent?.admitted_batch ||
          selectedStudent?.admitted_batch_name ||
          "";

        await Swal.fire({
          title: "Fee Terms Not Configured",
          html: `
      <div style="text-align:left">
        <p>Term fee configuration was not found for this student.</p>
        <p><b>Student:</b> ${String(studentName || "—")}</p>
        <p><b>Roll Number:</b> ${String(studentRollno)}</p>
        <p><b>Branch:</b> ${String(branchName)}</p>
        ${admittedBatch ? `<p><b>Admitted Batch:</b> ${String(admittedBatch)}</p>` : ""}
        <p>Please configure the term fees  for the corresponding  admitted batch and branch mapping .</p>
      </div>
    `,
          icon: "warning",
          confirmButtonText: "OK",
          confirmButtonColor: "#1976d2",
          didOpen: () => {
            Swal.getContainer()?.style.setProperty("z-index", "20000", "important");
          },
        });

        return; // Do not open the term fee dialog.
      }

      // Open the dialog only when term configurations are available.
      setTermYears(years);
      setTermDialogOpen(true);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to check the term fee configuration.";

      await Swal.fire({
        title: "Unable to Check Fee Terms",
        text: message,
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#1976d2",
        didOpen: () => {
          Swal.getContainer()?.style.setProperty("z-index", "20000", "important");
        },
      });
    } finally {
      setTermLoading(false);
    }
  };

  const refreshTermSummary = async () => {
    setTermLoading(true);
    setTermError("");

    try {
      const result = await getTermFeeSummary();
      setTermYears(result.years);
    } catch (error) {
      setTermError(
        error?.response?.data?.message || error?.message || "Unable to refresh term fee details."
      );
    } finally {
      setTermLoading(false);
    }
  };

  const getFirstDueTerm = (year) =>
    (year?.terms || []).find((term) => amountValue(term.balance_due) > 0.009);

  const openTermPayment = (year, term) => {
    if (!year || !term) return;

    const due = amountValue(term.balance_due);

    if (due <= 0.009) {
      Swal.fire({
        title: "No Due",
        text: "There is no outstanding amount for this term.",
        icon: "info",
        confirmButtonText: "OK",
        confirmButtonColor: "#1976d2",
        didOpen: () => {
          Swal.getContainer()?.style.setProperty("z-index", "20000", "important");
        },
      });
      return;
    }

    setSelectedTerm({ year, term });

    setPayment({
      amount: due.toFixed(2),
      payment_mode: "CASH",
      transaction_number: "",
      transaction_date: today(),
      remarks: "",
    });

    setPaymentDialogOpen(true);
  };

  const closePaymentDialog = () => {
    if (termSaving) return;

    setPaymentDialogOpen(false);
    setSelectedTerm(null);
  };

  const updatePayment = (field, value) => {
    setPayment((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const openPdfPreview = async (receiptId, printWindow = null) => {
    if (!receiptId) {
      throw new Error("Receipt ID is missing.");
    }

    const url = `${API_BASE_URL}/api/feereceipt-multiple/` + `${clientId}/${collegeId}/preview-pdf`;

    let pdfWindow = printWindow;

    try {
      const response = await axios.get(url, {
        params: {
          receiptIds: String(receiptId),
        },
        headers: getAuthHeaders(),
        responseType: "blob",
        timeout: 30000,
      });

      const contentType = response.headers?.["content-type"] || "";

      if (
        contentType.includes("application/json") ||
        (response.data instanceof Blob && response.data.size === 0)
      ) {
        const message =
          response.data instanceof Blob
            ? await response.data.text()
            : "The server returned an empty receipt.";

        throw new Error(message || "Unable to generate receipt PDF.");
      }

      const pdfBlob = new Blob([response.data], {
        type: "application/pdf",
      });

      // Check whether the server actually returned a PDF.
      if (pdfBlob.size === 0) {
        throw new Error("The generated receipt PDF is empty.");
      }

      const pdfUrl = URL.createObjectURL(pdfBlob);

      if (pdfWindow && !pdfWindow.closed) {
        pdfWindow.location.href = pdfUrl;
      } else {
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

      // Keep the Blob URL alive long enough for the browser to load it.
      window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 120000);
    } catch (error) {
      if (pdfWindow && !pdfWindow.closed) {
        pdfWindow.close();
      }

      throw error;
    }
  };

  const submitTermPayment = async () => {
    if (!selectedTerm) {
      Swal.fire({
        title: "Select Term",
        text: "Please select a term to pay.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#1976d2",
        didOpen: () => {
          Swal.getContainer()?.style.setProperty("z-index", "20000", "important");
        },
      });
      return;
    }

    const { year, term } = selectedTerm;
    const amount = amountValue(payment.amount);
    const due = amountValue(term.balance_due);
    const mode = String(payment.payment_mode || "").toUpperCase();

    if (!Number.isFinite(amount) || amount <= 0) {
      Swal.fire({
        title: "Invalid Amount",
        text: "Enter a payment amount greater than zero.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#1976d2",
        didOpen: () => {
          Swal.getContainer()?.style.setProperty("z-index", "20000", "important");
        },
      });
      return;
    }

    if (amount > due + 0.001) {
      Swal.fire({
        title: "Amount Exceeds Due",
        text: `Maximum payable amount for this term is ₹${money(due)}.`,
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#1976d2",
        didOpen: () => {
          Swal.getContainer()?.style.setProperty("z-index", "20000", "important");
        },
      });
      return;
    }

    if (mode !== "CASH") {
      if (!payment.transaction_number.trim()) {
        Swal.fire({
          title: "Transaction Number Required",
          text: "Enter the transaction/reference number for non-cash payments.",
          icon: "warning",
          confirmButtonText: "OK",
          confirmButtonColor: "#1976d2",
          didOpen: () => {
            Swal.getContainer()?.style.setProperty("z-index", "20000", "important");
          },
        });

        return;
      }

      if (!payment.transaction_date) {
        Swal.fire({
          title: "Transaction Date Required",
          text: "Select the transaction date for non-cash payments.",
          icon: "warning",
          confirmButtonText: "OK",
          confirmButtonColor: "#1976d2",
          didOpen: () => {
            Swal.getContainer()?.style.setProperty("z-index", "20000", "important");
          },
        });

        return;
      }
    }
    //const receiptWindow = window.open("about:blank", "_blank");
    const confirmation = await Swal.fire({
      title: "Confirm Term Fee Payment",
      html:
        `<div style="text-align:left">` +
        `<p><b>Student:</b> ${String(studentRollno)}</p>` +
        `<p><b>Academic Year:</b> ${String(year.academic_year || "")}</p>` +
        `<p><b>Term:</b> Term-${Number(term.termno)}</p>` +
        `<p><b>Amount:</b> ₹${money(amount)}</p>` +
        `<p><b>Payment Mode:</b> ${mode}</p>` +
        `</div>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Confirm Payment",
      cancelButtonText: "Cancel",
      cancelButtonColor: "#d33",
      confirmButtonColor: "#1976d2",
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) {
          container.style.zIndex = "20000";
        }
      },
    });
    if (!confirmation.isConfirmed) {
      return;
    }
    setTermSaving(true);

    let paymentSaved = false;
    let receiptId = null;

    try {
      const url = `${API_BASE_URL}/api/fee-receipt-terms/` + `${clientId}/${collegeId}/payment`;

      const payload = {
        student_rollno: String(studentRollno),
        year_number: Number(year.year_number),
        termno: Number(term.termno),
        fee_amount: amount,
        payment_mode: mode,
        transaction_number: mode === "CASH" ? null : payment.transaction_number.trim(),
        transaction_date: mode === "CASH" ? null : payment.transaction_date,
        remarks: payment.remarks.trim() || null,
      };

      const response = await axios.post(url, payload, {
        headers: getAuthHeaders(),
      });

      const result = getResponseData(response);

      receiptId = result?.fee_receipt_id ?? result?.data?.fee_receipt_id ?? null;

      if (!receiptId) {
        throw new Error(
          "The payment response did not contain a receipt ID. Verify the payment before retrying."
        );
      }

      paymentSaved = true;

      // Stop the spinner as soon as payment is confirmed saved.
      setTermSaving(false);

      setPaymentDialogOpen(false);
      setSelectedTerm(null);

      // Generate/open the receipt before running refresh operations.
      try {
        await openPdfPreview(receiptId);
      } catch (pdfError) {
        console.error("Receipt PDF preview failed:", pdfError);

        Swal.fire({
          title: "Payment Saved",
          text: `Payment was saved successfully, but the receipt PDF failed: ${
            pdfError.message || "Unknown PDF error"
          }. Please check the server PDF endpoint.`,
          icon: "warning",
          confirmButtonText: "OK",
          confirmButtonColor: "#1976d2",
          didOpen: () => {
            Swal.getContainer()?.style.setProperty("z-index", "20000", "important");
          },
        });
      }

      // Refresh independently so a refresh problem cannot block PDF generation.
      try {
        await refreshTermSummary();
      } catch (refreshError) {
        console.error("Term fee refresh failed:", refreshError);
      }

      try {
        if (typeof onPaymentSuccess === "function") {
          await Promise.race([
            Promise.resolve().then(() => onPaymentSuccess()),
            new Promise((_, reject) =>
              window.setTimeout(() => reject(new Error("Student fee refresh timed out.")), 10000)
            ),
          ]);
        }
      } catch (callbackError) {
        console.error("Payment success callback failed:", callbackError);
      }
      await Swal.fire({
        title: "Payment Saved",
        text: `Term fee payment of ₹${money(amount)} was recorded successfully.`,
        icon: "success",
        confirmButtonText: "Continue",
        didOpen: () => {
          Swal.getContainer()?.style.setProperty("z-index", "20000", "important");
        },
      });

      try {
        await openPdfPreview(receiptId);
      } catch (pdfError) {
        console.error("Receipt PDF preview failed:", pdfError);

        Swal.fire(
          "Payment Saved",
          "The payment was saved, but the receipt PDF could not be opened. Please use the receipt print option after checking the saved receipt.",
          "warning"
        );
      }
    } catch (error) {
      console.error("Term fee payment error:", error);

      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to process the term fee payment.";

      if (paymentSaved) {
        Swal.fire({
          title: "Payment Saved",
          text: "The payment was saved, but a later step failed. Do not submit the same payment again until you verify the receipt.",
          icon: "warning",
          confirmButtonText: "OK",
          confirmButtonColor: "#1976d2",
          didOpen: () => {
            Swal.getContainer()?.style.setProperty("z-index", "20000", "important");
          },
        });
      } else {
        Swal.fire({
          title: "Payment Failed",
          text: message,
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#1976d2",
          didOpen: () => {
            Swal.getContainer()?.style.setProperty("z-index", "20000", "important");
          },
        });
      }
    } finally {
      setTermSaving(false);
    }
  };

  useEffect(() => {
    if (!termDialogOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape" && !termSaving) {
        setPaymentDialogOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [termDialogOpen, termSaving]);

  const renderTermCell = (year, termno) => {
    if (!year) {
      return <Typography color="text.secondary">—</Typography>;
    }

    const term = year.terms?.find((item) => Number(item.termno) === termno);

    if (!term) {
      return <Typography color="text.secondary">Not configured</Typography>;
    }

    const collected = amountValue(term.collected_amount);
    const configured = amountValue(term.configured_fee);
    const due = amountValue(term.balance_due);
    const concessionApplied = Boolean(term.concession_applied);
    const allocatedConcession = amountValue(term.allocated_concession);
    return (
      <Stack spacing={0.4} sx={{ minWidth: 145 }}>
        <Typography fontWeight={700} fontSize="0.93rem">
          <Box component="span" sx={{ color: "success.main" }}>
            ₹{money(collected)}
          </Box>{" "}
          <br />
          {" of "}
          <Box component="span" sx={{ color: "error.main" }}>
            ₹{money(configured)}
          </Box>
          {concessionApplied && (
            <>
              <br />
              <Chip
                label={`Concession: (₹${money(allocatedConcession)})`}
                color="primary"
                size="small"
                variant="outlined"
                sx={{
                  color: "#1976d2",
                  borderColor: "#1976d2",
                  fontWeight: 700,
                  backgroundColor: "#e3f2fd",
                }}
              />
            </>
          )}
        </Typography>

        <Typography fontSize="0.98rem" color={due > 0.009 ? "secondary.main" : "success.main"}>
          {due > 0.009 ? `Due: ₹${money(due)}` : "No Due"}
        </Typography>
      </Stack>
    );
  };

  const renderAnnualAmount = (value) => (
    <Typography fontWeight={700} fontSize="0.85rem">
      ₹{money(value)}
    </Typography>
  );

  const renderAction = (year) => {
    if (!year) {
      return <Typography color="text.secondary">—</Typography>;
    }

    const dueTerm = getFirstDueTerm(year);

    if (!dueTerm) {
      return (
        <Typography color="success.main" fontWeight={700}>
          No Due
        </Typography>
      );
    }

    return (
      <Stack spacing={0.5} alignItems="center">
        <Button
          variant="contained"
          color="primary"
          size="small"
          align="center"
          startIcon={<PaymentsIcon />}
          onClick={() => openTermPayment(year, dueTerm)}
          sx={{
            color: "#fff",
            fontWeight: 700,
            backgroundColor: "#1976d2",
            whiteSpace: "nowrap",
            "&:hover": {
              backgroundColor: "#1565c0",
            },
            "& .MuiButton-startIcon": {
              color: "#fff",
            },
          }}
        >
          Pay Now
        </Button>

        <Typography fontSize="0.72rem" color="text.secondary">
          Term-{dueTerm.termno} Due
        </Typography>
      </Stack>
    );
  };

  const renderYearCell = (row, year) => {
    if (row.type === "term") {
      return renderTermCell(year, row.termno);
    }

    if (row.type === "concession") {
      return renderAnnualAmount(year?.annual_concession);
    }

    if (row.type === "refund") {
      return renderAnnualAmount(year?.annual_refund);
    }

    if (row.type === "action") {
      return renderAction(year);
    }

    return null;
  };

  const comparisonRows = [
    { id: "term-1", label: "Term-1", type: "term", termno: 1 },
    { id: "term-2", label: "Term-2", type: "term", termno: 2 },
    { id: "term-3", label: "Term-3", type: "term", termno: 3 },
    { id: "concession", label: "Concession", type: "concession" },
    { id: "refund", label: "Refund", type: "refund" },
    { id: "action", label: "Action", type: "action" },
  ];

  const selectedYear = selectedTerm?.year;
  const selectedTermDetails = selectedTerm?.term;
  const paymentModeIsCash = payment.payment_mode === "CASH";

  return (
    <>
      <Button
        variant="contained"
        fullWidth
        startIcon={<ReceiptLongIcon />}
        disabled={!selectedStudent || termLoading || termSaving}
        onClick={openTermFeeDialog}
        sx={{
          color: "#fff",
          backgroundColor: "#1976d2",
          fontWeight: 700,
          minHeight: 42,
          fontSize: "0.78rem",
          whiteSpace: "normal",
          lineHeight: 1.3,
          px: 0.75,
          "&:hover": {
            backgroundColor: "#1565c0",
          },
          "& .MuiButton-startIcon": {
            mr: 0.5,
            flexShrink: 0,
          },
        }}
      >
        Show Term Fees
      </Button>

      {/* Term fee comparison dialog */}

      <Dialog
        open={termDialogOpen}
        onClose={(event, reason) => {
          // Prevent closing on outside click or Escape
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }

          if (!termSaving) {
            setTermDialogOpen(false);
            setTermError("");
          }
        }}
        disableEscapeKeyDown
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 400,
            pr: 1,
            py: 0.5,
            pl: 2,

            minHeight: "36px",
            boxSizing: "border-box",
            backgroundColor: "#3781e2",
            color: "#fff",
            fontWeight: 700,
          }}
        >
          STUDENT FEE TERM DETAILS
          <IconButton
            onClick={() => {
              if (!termSaving) {
                setTermDialogOpen(false);
                setTermError("");
              }
            }}
            disabled={termSaving}
            aria-label="Close student term fee dialog"
            sx={{
              color: "#d32f2f",
              backgroundColor: "#ffebee",
              "&:hover": {
                backgroundColor: "#ffebee",
                color: "#b71c1c",
              },
              "&.Mui-disabled": {
                color: "#ef9a9a",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography fontWeight={700}>Student: {studentName || "—"}</Typography>
              <Typography color="text.secondary">
                Roll Number: {studentRollno || "—"} Branch: {studentBranch || "—"}
              </Typography>
            </Box>

            <Button
              variant="outlined"
              onClick={refreshTermSummary}
              disabled={termLoading || termSaving}
              sx={{
                color: "#1976d2",
                borderColor: "#1976d2",
                fontWeight: 700,
                "&:hover": {
                  backgroundColor: "#e3f2fd",
                  borderColor: "#1565c0",
                },
                "&.Mui-disabled": {
                  color: "#90caf9",
                  borderColor: "#bbdefb",
                },
              }}
            >
              Refresh
            </Button>
          </Stack>

          {termLoading && (
            <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 5 }}>
              <CircularProgress />
              <Typography>Loading term fees...</Typography>
            </Stack>
          )}

          {!termLoading && termError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {termError}
            </Alert>
          )}

          {!termLoading && !termError && termYears.length > 0 && (
            <Box sx={{ width: "100%", minWidth: 0 }}>
              {/* Academic year header cards - unchanged */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: `repeat(${Math.min(termYears.length, 2)}, minmax(0, 1fr))`,
                    lg: `repeat(${Math.min(termYears.length, 3)}, minmax(0, 1fr))`,
                  },
                  gap: 2,
                  mb: 2,
                }}
              >
                {termYears.map((year) => (
                  <Paper
                    key={year.year_number}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderTop: "4px solid #1976d2",
                      backgroundColor: "#f5faff",
                    }}
                  >
                    <Typography fontWeight={600} color="info.main">
                      {getYearLabel(year)} {year.academic_year}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      Annual Fee: ₹{money(year.total_fee)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Concession: ₹{money(year.annual_concession)}
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      Collected: ₹{money(year.total_collected)}
                    </Typography>

                    <Typography variant="body2" color="error.main">
                      Annual Due: ₹{money(year.annual_balance_due)}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              {/* Fee comparison - fixed aligned columns */}
              <Paper
                variant="outlined"
                sx={{
                  width: "100%",
                  overflowX: "auto",
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    minWidth: `${200 + termYears.length * 180}px`,
                  }}
                >
                  {/* Header row */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: `100px repeat(${termYears.length}, minmax(100px, 1fr))`,
                      backgroundColor: "#1976d2",
                      color: "#fff",
                    }}
                  >
                    <Box sx={{ px: 1.5, py: 1.25, fontWeight: 700 }}>Description</Box>

                    {termYears.map((year) => (
                      <Box
                        key={year.year_number}
                        sx={{
                          px: 1,
                          py: 1.25,
                          fontWeight: 700,
                          textAlign: "center",
                        }}
                      >
                        {getYearLabel(year)}
                      </Box>
                    ))}
                  </Box>

                  {/* Fee rows */}
                  {comparisonRows.map((row, index) => (
                    <Box
                      key={row.id}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: `110px repeat(${termYears.length}, minmax(110px, 1fr))`,
                        alignItems: "center",
                        minHeight: 48,
                        backgroundColor: index % 2 === 0 ? "#fff" : "#f8fbff",
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    >
                      {/* Fee description */}
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1,
                          fontWeight: 600,
                        }}
                      >
                        {row.label}
                      </Box>

                      {/* Dynamic academic-year values */}
                      {termYears.map((year) => (
                        <Box
                          key={`${row.id}-${year.year_number}`}
                          sx={{
                            px: 1,
                            py: 1,
                            textAlign: "center",
                            borderLeft: "1px solid #eee",
                            minWidth: 0,
                          }}
                        >
                          {renderYearCell(row, year)}
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Box>
          )}

          {!termLoading && !termError && termYears.length === 0 && (
            <Alert severity="info">
              No term fee data is available for this students admitted batch.
            </Alert>
          )}

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            Academic years are derived by the backend from the students admitted batch. Concession
            and refund values shown above are annual amounts.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setTermDialogOpen(false)}
            disabled={termSaving}
            variant="contained"
            sx={{
              backgroundColor: "#d32f2f",
              color: "#fff",
              fontWeight: 700,
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Selected term payment dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          closePaymentDialog();
        }}
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pr: 1,
            py: 0.5,
            pl: 2,
            backgroundColor: "#3781e2",
            color: "#fff",
            fontWeight: 700,
          }}
        >
          Pay Term Fee
          <IconButton
            onClick={closePaymentDialog}
            sx={{
              color: "#d32f2f",
              backgroundColor: "#ffebee",
              "&:hover": {
                backgroundColor: "#ffebee",
                color: "#b71c1c",
              },
            }}
            aria-label="Close payment dialog"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedYear && selectedTermDetails && (
            <>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: "#f8fbff" }}>
                <Grid container spacing={0}>
                  <Grid item xs={12} sm={4}>
                    <Typography color="text.secondary" fontSize="0.8rem">
                      Year
                    </Typography>
                    <Typography fontWeight={400} fontSize="0.9rem">
                      {selectedYear.year_number}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography color="text.secondary" fontSize="0.8rem">
                      Academic Year
                    </Typography>
                    <Typography fontWeight={400} fontSize="0.9rem">
                      {selectedYear.academic_year}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography color="text.secondary" fontSize="0.8rem">
                      Fee Term
                    </Typography>
                    <Typography fontWeight={400} fontSize="0.9rem">
                      Term-{selectedTermDetails.termno}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography color="text.secondary" fontSize="0.8rem">
                      Actual Term Fee
                    </Typography>
                    <Typography fontWeight={400} fontSize="0.9rem">
                      ₹{money(selectedTermDetails.configured_fee)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography color="text.secondary" fontSize="0.8rem">
                      Already Paid
                    </Typography>
                    <Typography fontWeight={400} color="success.main" fontSize="0.9rem">
                      ₹{money(selectedTermDetails.collected_amount)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography color="text.secondary" fontSize="0.8rem">
                      Outstanding Term Due
                    </Typography>
                    <Typography fontWeight={700} color="error.main" fontSize="0.9rem">
                      ₹{money(selectedTermDetails.balance_due)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    size="medium"
                    label="Payment Amount"
                    type="number"
                    value={payment.amount}
                    onChange={(event) => updatePayment("amount", event.target.value)}
                    inputProps={{
                      min: 0.01,
                      max: amountValue(selectedTermDetails.balance_due),
                      step: "0.01",
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 60,
                        fontSize: "1.2rem",
                        fontWeight: 600,
                      },
                      "& .MuiInputBase-input": {
                        padding: "16px 14px",
                        fontSize: "1.2rem",
                        fontWeight: 600,
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "1.05rem",
                        fontWeight: 600,
                      },
                      "& .MuiInputLabel-shrink": {
                        fontSize: "1.1rem",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Payment Mode"
                    value={payment.payment_mode}
                    onChange={(event) => updatePayment("payment_mode", event.target.value)}
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                  >
                    <option value="CASH">Cash</option>
                    <option value="ONLINE">Online</option>
                    <option value="DD">DD</option>
                    <option value="CHEQUE">Cheque</option>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Transaction / Reference Number"
                    value={payment.transaction_number}
                    onChange={(event) => updatePayment("transaction_number", event.target.value)}
                    disabled={paymentModeIsCash}
                    required={!paymentModeIsCash}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Transaction Date"
                    type="date"
                    value={payment.transaction_date}
                    onChange={(event) => updatePayment("transaction_date", event.target.value)}
                    disabled={paymentModeIsCash}
                    required={!paymentModeIsCash}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Remarks"
                    value={payment.remarks}
                    onChange={(event) => updatePayment("remarks", event.target.value)}
                    inputProps={{ maxLength: 500 }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          {/* Cancel - Red */}
          <Button
            variant="contained"
            onClick={closePaymentDialog}
            disabled={termSaving}
            sx={{
              backgroundColor: "#d32f2f",
              color: "#fff",
              fontWeight: 700,
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
            }}
          >
            Cancel
          </Button>

          {/* Pay and Print Receipt - Blue */}
          <Button
            variant="contained"
            startIcon={termSaving ? <CircularProgress size={18} color="inherit" /> : <PrintIcon />}
            onClick={submitTermPayment}
            disabled={termSaving || !selectedTerm}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              fontWeight: 700,
              "&:hover": {
                backgroundColor: "#1565c0",
              },
              "&.Mui-disabled": {
                backgroundColor: "#90caf9",
                color: "#fff",
              },
            }}
          >
            {termSaving ? "Processing Payment..." : "Pay and Print Receipt"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
FeeReceiptTermsDialog.propTypes = {
  selectedStudent: PropTypes.shape({
    student_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    student_rollno: PropTypes.string,
    student_roll_no: PropTypes.string,
    rollno: PropTypes.string,
    student_name: PropTypes.string,
    name: PropTypes.string,

    branch: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        branch_name: PropTypes.string,
      }),
    ]),

    admittedbatch: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        admittedbatch: PropTypes.string,
      }),
    ]),

    admitted_batch: PropTypes.string,
    admitted_batch_name: PropTypes.string,
  }),

  onPaymentSuccess: PropTypes.func,
};

FeeReceiptTermsDialog.defaultProps = {
  selectedStudent: null,
  onPaymentSuccess: () => {},
};
